// Phase PACKER-1.1 · Full-screen QR / barcode scanner for the packer portal.
//
// Behaviour
//   • Live camera scan via `html5-qrcode` (rear camera preferred).
//   • Manual entry fallback below the camera viewport.
//   • Resolves every tracking_id via `GET /api/packer/lookup-by-scan`.
//     - 200 → navigate to /packer/parcel/<tid>
//     - 404 + code:"NOT_FOUND"     → red sticky "Tracking ID not recognized" banner
//     - 404 + code:"NOT_ASSIGNED"  → red sticky "This parcel is not assigned to you" banner
//   • Wrong-parcel errors are NEVER a transient toast — they are a sticky
//     red banner that echoes the scanned tracking_id back. User must
//     explicitly press "Scan Again" to dismiss before the next scan.
//
// Camera access requires HTTPS — the preview environment provides it.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, AlertOctagon, ScanLine, Loader2, RefreshCw, Flashlight, FlashlightOff } from "lucide-react";
import api from "../../services/api";

const ORANGE = "#FF6B00";

// Light validator — accepts the canonical PKG- prefix or 6+ uppercase
// alphanumeric tokens (covers numeric-only barcodes too).
const TID_RX = /^[A-Z0-9-]{6,}$/i;

export default function PackerScanModal({ open, onClose }) {
  const navigate = useNavigate();
  const containerId = "packer-scan-camera";
  const scannerRef = useRef(null);
  const [cameraState, setCameraState] = useState("idle"); // idle | starting | running | denied | error
  const [cameraErr, setCameraErr] = useState("");
  const [manualTid, setManualTid] = useState("");
  const [resolving, setResolving] = useState(false);
  // Sticky error banner state — NEVER a toast.
  // shape: { tid, code: "NOT_FOUND" | "NOT_ASSIGNED", message }
  const [stickyErr, setStickyErr] = useState(null);
  // Torch (flashlight) state. `supported=false` means the camera track does
  // not expose torch — desktops, most laptops, and some browsers. The button
  // is HIDDEN entirely in that case (per brief, no disabled state shown).
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  // Probe the live MediaStreamTrack for torch capability whenever the
  // scanner enters the running state.
  const probeTorch = useCallback(() => {
    try {
      // html5-qrcode exposes the underlying <video> stream
      const vid = document.querySelector(`#${containerId} video`);
      const track = vid?.srcObject?.getVideoTracks?.()[0];
      const caps = track?.getCapabilities?.();
      setTorchSupported(!!caps && "torch" in caps);
    } catch { setTorchSupported(false); }
  }, []);

  const toggleTorch = useCallback(async () => {
    try {
      const vid = document.querySelector(`#${containerId} video`);
      const track = vid?.srcObject?.getVideoTracks?.()[0];
      if (!track) return;
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      // If apply fails (browser quirk), hide the button so we don't lie
      // about capability.
      setTorchSupported(false);
    }
  }, [torchOn]);

  const stop = useCallback(async () => {
    try {
      if (scannerRef.current) {
        const state = scannerRef.current.getState?.();
        // Html5Qrcode.NOT_STARTED = 1, SCANNING = 2, PAUSED = 3
        if (state === 2 || state === 3) await scannerRef.current.stop();
        await scannerRef.current.clear?.();
      }
    } catch {
      /* swallow — scanner cleanup is best-effort */
    } finally {
      scannerRef.current = null;
    }
  }, []);

  // Resolve a tracking_id via the backend.
  const resolve = useCallback(async (rawTid) => {
    const tid = (rawTid || "").trim().toUpperCase();
    if (!tid) return;
    if (!TID_RX.test(tid)) {
      setStickyErr({
        tid,
        code: "INVALID",
        message: "Looks like an invalid tracking ID. Tap Scan Again to retry.",
      });
      return;
    }
    setResolving(true);
    setStickyErr(null);
    try {
      const r = await api.get("/packer/lookup-by-scan",
                                { params: { tracking_id: tid } });
      if (r.data?.ok) {
        await stop();
        onClose?.();
        navigate(`/packer/parcel/${tid}`);
      }
    } catch (e) {
      // FastAPI HTTPException(detail={...}) → response.data.detail is the dict.
      const raw = e?.response?.data?.detail;
      let code = "NOT_FOUND";
      let message = "Tracking ID not recognized.";
      if (raw && typeof raw === "object") {
        code = raw.code || code;
        message = raw.detail || raw.message || message;
      } else if (typeof raw === "string") {
        message = raw;
      } else if (e?.userMessage) {
        message = e.userMessage;
      }
      setStickyErr({ tid, code, message });
      // Pause the live scanner so it stops firing on the same broken code.
      try {
        if (scannerRef.current?.getState?.() === 2) {
          await scannerRef.current.pause(true);
        }
      } catch { /* noop */ }
    } finally {
      setResolving(false);
    }
  }, [navigate, onClose, stop]);

  // Start the live camera scanner when the modal opens.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCameraState("starting");
    setCameraErr("");
    setStickyErr(null);
    setManualTid("");
    const start = async () => {
      try {
        // Lazy-construct so we never touch getUserMedia on hidden modals.
        const html5 = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = html5;
        await html5.start(
          { facingMode: { exact: "environment" } },
          { fps: 10, qrbox: { width: 240, height: 240 },
            aspectRatio: 1.0 },
          (decoded) => {
            // De-dupe rapid-fire decoded events while a sticky error is up.
            if (cancelled) return;
            resolve(decoded);
          },
          () => { /* per-frame "not-found" — quiet */ }
        );
        if (cancelled) return;
        setCameraState("running");
        // Probe torch on a brief delay so the video track is fully ready.
        setTimeout(probeTorch, 400);
      } catch (e1) {
        // Fall back to user-facing camera if rear camera isn't available
        try {
          const html5 = scannerRef.current
                          || new Html5Qrcode(containerId, { verbose: false });
          scannerRef.current = html5;
          await html5.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 240, height: 240 } },
            (decoded) => { if (!cancelled) resolve(decoded); },
            () => {}
          );
          if (cancelled) return;
          setCameraState("running");
          setTimeout(probeTorch, 400);
        } catch (e2) {
          const msg = String(e2?.message || e1?.message || "");
          const denied = /permission|denied|notallowed/i.test(msg);
          setCameraState(denied ? "denied" : "error");
          setCameraErr(denied
            ? "Camera permission denied. Use Manual Entry below or grant access in your browser settings."
            : "Could not start camera. Use Manual Entry below.");
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      stop();
    };
  }, [open, resolve, stop]);

  const onScanAgain = useCallback(async () => {
    setStickyErr(null);
    setManualTid("");
    try {
      if (scannerRef.current?.getState?.() === 3) {
        await scannerRef.current.resume();
      }
    } catch { /* noop */ }
  }, []);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black text-white flex flex-col"
         role="dialog" aria-modal="true" aria-label="Scan parcel QR / barcode"
         data-testid="packer-scan-modal">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 shrink-0">
        <ScanLine className="h-5 w-5" style={{ color: ORANGE }} aria-hidden="true"/>
        <span className="font-['Outfit'] text-base font-semibold">Scan parcel</span>
        <span className="text-[10px] text-white/50 ml-1">QR · 1D barcode</span>
        {torchSupported && (
          <button onClick={toggleTorch}
                  aria-label={torchOn ? "Turn flashlight off" : "Turn flashlight on"}
                  aria-pressed={torchOn}
                  data-testid="packer-scan-torch"
                  data-torch-on={torchOn ? "true" : "false"}
                  className={"ml-auto p-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] transition-colors " +
                              (torchOn ? "bg-[#FF6B00] text-white" : "text-white/80 hover:bg-white/10")}>
            {torchOn ? <Flashlight className="h-5 w-5"/> : <FlashlightOff className="h-5 w-5"/>}
          </button>
        )}
        <button onClick={onClose}
                aria-label="Close"
                data-testid="packer-scan-close"
                className={(torchSupported ? "" : "ml-auto ") + "p-2 rounded-full hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]"}>
          <X className="h-5 w-5"/>
        </button>
      </div>

      {/* Sticky red banner — NEVER auto-dismisses */}
      {stickyErr && (
        <div className="bg-rose-600 text-white px-3 py-2.5 border-b border-rose-700"
             data-testid="packer-scan-error-banner"
             data-error-code={stickyErr.code}
             role="alert">
          <div className="flex items-start gap-2">
            <AlertOctagon className="h-5 w-5 shrink-0 mt-0.5"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">
                {stickyErr.code === "NOT_ASSIGNED"
                  ? "This parcel is not assigned to you"
                  : stickyErr.code === "INVALID"
                    ? "Invalid tracking ID"
                    : "Tracking ID not recognized"}
              </div>
              <div className="text-xs mt-0.5 break-all">
                Scanned:{" "}
                <span className="font-mono bg-rose-800/70 px-1.5 py-0.5 rounded"
                      data-testid="packer-scan-error-tid">{stickyErr.tid}</span>
              </div>
              <div className="text-xs mt-1 opacity-90">{stickyErr.message}</div>
            </div>
            <button onClick={onScanAgain}
                    data-testid="packer-scan-again"
                    className="shrink-0 inline-flex items-center gap-1 bg-white text-rose-700 hover:bg-rose-50 font-semibold text-xs px-3 py-1.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <RefreshCw className="h-3.5 w-3.5"/> Scan Again
            </button>
          </div>
        </div>
      )}

      {/* Camera viewport */}
      <div className="relative flex-1 min-h-0 flex flex-col items-center justify-start bg-black overflow-hidden">
        <div id={containerId}
             data-testid="packer-scan-camera"
             className="w-full max-w-md aspect-square bg-black/60"/>
        {cameraState === "starting" && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/80 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: ORANGE }}/>
            Starting camera…
          </div>
        )}
        {(cameraState === "denied" || cameraState === "error") && (
          <div className="absolute top-4 left-3 right-3 bg-amber-500/95 text-amber-950 rounded-lg px-3 py-2 text-xs"
               data-testid="packer-scan-camera-error">
            <Camera className="h-4 w-4 inline mr-1 -mt-0.5"/>
            {cameraErr}
          </div>
        )}
        {/* Aim reticle hint */}
        {cameraState === "running" && !stickyErr && (
          <div className="absolute bottom-3 left-0 right-0 text-center text-[11px] text-white/70">
            Align the QR/barcode within the box
          </div>
        )}
      </div>

      {/* Manual entry */}
      <form
        className="border-t border-white/10 bg-black px-3 py-2.5 shrink-0"
        onSubmit={(e) => { e.preventDefault(); resolve(manualTid); }}
        data-testid="packer-scan-manual-form"
      >
        <label className="block text-[10px] uppercase tracking-wider text-white/60 mb-1">
          Manual entry
        </label>
        <div className="flex items-center gap-2">
          <input
            value={manualTid}
            onChange={(e) => setManualTid(e.target.value.toUpperCase())}
            placeholder="e.g. PKG-P1412C03"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            data-testid="packer-scan-manual-input"
            className="flex-1 bg-white/10 border border-white/20 focus:border-[#FF6B00] focus:outline-none rounded-lg px-3 py-2 text-sm font-mono placeholder:text-white/40"
          />
          <button type="submit"
                  disabled={resolving || !manualTid.trim()}
                  data-testid="packer-scan-manual-submit"
                  className="text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white"
                  style={{ backgroundColor: ORANGE }}>
            {resolving ? <Loader2 className="h-4 w-4 inline animate-spin"/> : "Look up"}
          </button>
        </div>
      </form>
    </div>
  );
}
